/**
 * Client-side tier/entitlement state. Loaded once from `GET /billing/me`
 * after login; exposed as a reactive store for components to gate UI.
 *
 * Components should prefer `canUseFeature('kiwisaver')` over raw matrix
 * lookups so personal-app users bypass correctly.
 */

import { derived, writable, type Readable } from 'svelte/store';
import { api, ApiError, type BillingMeResponse, type BillingTier } from './api.js';

// ─── Types (mirror backend) ───────────────────────────────────────────────────

export type FeatureKey =
  | 'roundups_multi_destination'
  | 'roundup_target_multi'
  | 'balance_watch_rule'
  | 'chained_rule'
  | 'kiwisaver'
  | 'subscription_tracker'
  | 'goal_split_contributions'
  | 'csv_export'
  | 'all_push_notifications'
  | 'multi_user_household';

export type LimitKey =
  | 'linked_accounts'
  | 'tracked_roundup_accounts'
  | 'active_payment_rules'
  | 'recurring_standing_orders'
  | 'savings_goals'
  | 'passkeys'
  | 'monthly_successful_payments';

export type TierFeatures = Record<FeatureKey, boolean>;
export type TierLimits = Record<LimitKey, number | null>;

/**
 * The structural response from `/billing/me`. We reuse `BillingMeResponse` as
 * the wire-level shape (with loose `Record<string, ...>` features / limits)
 * and narrow at access time via `canUseFeature` / `remainingLimit` below, so
 * adding a new feature key on the backend doesn't require a lockstep client
 * deploy just to compile.
 */
export type BillingMe = BillingMeResponse;

// ─── Store ────────────────────────────────────────────────────────────────────

interface TierState {
  loading: boolean;
  error: string | null;
  data: BillingMe | null;
}

const initial: TierState = { loading: false, error: null, data: null };

function createTierStore() {
  const store = writable<TierState>(initial);
  let lastLoaded = 0;
  let inflight: Promise<BillingMe | null> | null = null;

  async function refresh(): Promise<BillingMe | null> {
    if (inflight) return inflight;
    inflight = (async () => {
      store.update((s) => ({ ...s, loading: true, error: null }));
      try {
        const me = await api.billing.me();
        store.set({ loading: false, error: null, data: me });
        lastLoaded = Date.now();
        return me;
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Failed to load billing state';
        store.update((s) => ({ ...s, loading: false, error: msg }));
        return null;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  /** Refresh only if cached value is older than `maxAgeMs`. */
  async function ensure(maxAgeMs = 60_000): Promise<BillingMe | null> {
    if (Date.now() - lastLoaded < maxAgeMs) {
      let cached: BillingMe | null = null;
      store.subscribe((s) => { cached = s.data; })();
      if (cached) return cached;
    }
    return refresh();
  }

  function reset() {
    lastLoaded = 0;
    store.set(initial);
  }

  return {
    subscribe: store.subscribe,
    refresh,
    ensure,
    reset,
  };
}

export const tierStore = createTierStore();

// ─── Derived readables ────────────────────────────────────────────────────────

export const currentEntitlement: Readable<BillingMe | null> = derived(
  tierStore,
  ($s) => $s.data,
);

export const currentTier: Readable<BillingTier | null> = derived(
  tierStore,
  ($s) => $s.data?.tier ?? null,
);

export const isPersonal: Readable<boolean> = derived(
  tierStore,
  () => false,
);

// ─── Imperative helpers ───────────────────────────────────────────────────────
// Prefer these over subscribing manually in utility code. For component
// reactivity, derive a `$derived` off `currentEntitlement`.

/** True if the feature is available on the user's effective tier. */
export function canUseFeature(me: BillingMe | null, feature: FeatureKey): boolean {
  if (!me) return false;
  return me.features[feature] === true;
}

/** Remaining quota (null = unlimited). */
export function remainingLimit(
  me: BillingMe | null,
  limit: LimitKey,
): number | null {
  if (!me) return 0;
  const cap = me.limits[limit] ?? null;
  if (cap === null) return null;
  const used = usageFor(me, limit);
  return Math.max(0, cap - used);
}

/** True if user is at or over cap. */
export function atLimit(me: BillingMe | null, limit: LimitKey): boolean {
  if (!me) return true; // fail-closed when state unknown
  const cap = me.limits[limit] ?? null;
  if (cap === null) return false;
  return usageFor(me, limit) >= cap;
}

function usageFor(me: BillingMe, limit: LimitKey): number {
  switch (limit) {
    case 'linked_accounts':
      return me.usage.linked_accounts;
    case 'active_payment_rules':
      return me.usage.active_payment_rules;
    case 'savings_goals':
      return me.usage.savings_goals;
    case 'monthly_successful_payments':
      return me.usage.monthly_successful_payments;
    default:
      // Tracked-roundup-accounts, standing-orders, passkeys: no counter wired
      // yet — treated as 0 so gating deferral falls to the server.
      return 0;
  }
}

/**
 * Parse a 402 `tier_required` error body into a friendlier shape for prompts.
 * Usage:
 *   try { await api.foo() } catch (e) { const info = tierErrorInfo(e); if (info) showPrompt(info); }
 */
export interface TierErrorInfo {
  feature?: FeatureKey;
  limit?: LimitKey;
  current?: number;
  cap?: number;
  tier: BillingTier;
  upgradeTo: BillingTier;
}

export function tierErrorInfo(err: unknown): TierErrorInfo | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 402) return null;
  // The backend sends `{ error: 'tier_required', ... }` but `ApiError` only
  // carries `message` (the `error` field). Callers with the raw body will want
  // to re-inspect; for now we surface only what we know.
  return null;
}
